import React, { useState } from 'react';
import { PlusIcon, SearchIcon, FilterIcon, CheckIcon, XIcon, EditIcon, TrashIcon, ChevronLeftIcon, DownloadIcon, UploadIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Catalog, CatalogSchema, CatalogProduct } from '../CatalogsManagement';
import { ProductTable } from './ProductTable';
interface ProductManagerProps {
  catalog: Catalog;
  products: CatalogProduct[];
  schema: CatalogSchema | null;
  onAddProduct: () => void;
  onEditProduct: (product: CatalogProduct) => void;
  onToggleStatus: (productId: string) => void;
  onBack: () => void;
  locale: string;
}
export function ProductManager({
  catalog,
  products,
  schema,
  onAddProduct,
  onEditProduct,
  onToggleStatus,
  onBack,
  locale
}: ProductManagerProps) {
  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    manufacturer: '',
    minPrice: '',
    maxPrice: '',
    availability: ''
  });
  // Get unique categories and manufacturers for filter dropdowns
  const categories = [...new Set(products.map(p => p.category))].sort();
  const manufacturers = [...new Set(products.map(p => p.manufacturer))].sort();
  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  // Handle filter changes
  const handleFilterChange = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };
  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      category: '',
      manufacturer: '',
      minPrice: '',
      maxPrice: '',
      availability: ''
    });
    setSearchTerm('');
  };
  // Apply filters to products
  const getFilteredProducts = () => {
    return products.filter(product => {
      // Search term filter
      if (searchTerm && !product.name.toLowerCase().includes(searchTerm.toLowerCase()) && !product.vendorSku.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      // Category filter
      if (filters.category && product.category !== filters.category) {
        return false;
      }
      // Manufacturer filter
      if (filters.manufacturer && product.manufacturer !== filters.manufacturer) {
        return false;
      }
      // Price range filter
      if (filters.minPrice && product.price < parseFloat(filters.minPrice)) {
        return false;
      }
      if (filters.maxPrice && product.price > parseFloat(filters.maxPrice)) {
        return false;
      }
      // Availability filter
      if (filters.availability === 'in-stock' && product.availableStock <= 0) {
        return false;
      }
      if (filters.availability === 'out-of-stock' && product.availableStock > 0) {
        return false;
      }
      return true;
    });
  };
  // Handle bulk import
  const handleBulkImport = () => {
    // In a real app, this would open a file picker or modal
    alert('Bulk import functionality would be implemented here');
  };
  // Handle export
  const handleExport = () => {
    // In a real app, this would trigger a CSV/Excel download
    alert('Export functionality would be implemented here');
  };
  const filteredProducts = getFilteredProducts();
  return <div className="space-y-6" data-id="element-12375">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-12376">
        <div className="flex items-center" data-id="element-12377">
          <button onClick={onBack} className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12378">
            <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-12379" />
            Back
          </button>
          <div data-id="element-12380">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-12381">{catalog.name}</h2>
            <p className="text-sm text-gray-500" data-id="element-12382">
              Managing {products.length} products
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2" data-id="element-12383">
          <button onClick={handleExport} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12384">
            <DownloadIcon className="h-4 w-4 mr-1" data-id="element-12385" />
            Export
          </button>
          <button onClick={handleBulkImport} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12386">
            <UploadIcon className="h-4 w-4 mr-1" data-id="element-12387" />
            Bulk Import
          </button>
          <button onClick={onAddProduct} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12388">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-12389" />
            Add Product
          </button>
        </div>
      </div>
      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0" data-id="element-12390">
        <div className="flex items-center space-x-2" data-id="element-12391">
          <div className="relative rounded-md shadow-sm" data-id="element-12392">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12393">
              <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-12394" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search products..." value={searchTerm} onChange={handleSearch} data-id="element-12395" />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12396">
            <FilterIcon className="h-4 w-4 mr-1" data-id="element-12397" />
            Filters
            {(filters.category || filters.manufacturer || filters.minPrice || filters.maxPrice || filters.availability) && <span className="ml-1 px-1.5 py-0.5 bg-indigo-100 text-indigo-800 rounded-full text-xs" data-id="element-12398">
                {[filters.category ? 1 : 0, filters.manufacturer ? 1 : 0, filters.minPrice || filters.maxPrice ? 1 : 0, filters.availability ? 1 : 0].reduce((a, b) => a + b, 0)}
              </span>}
          </button>
          <button onClick={handleResetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12399">
            <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-12400" />
            Reset
          </button>
        </div>
        <div className="text-sm text-gray-500" data-id="element-12401">
          Showing {filteredProducts.length} of {products.length} products
        </div>
      </div>
      {/* Filters panel */}
      {showFilters && <div className="bg-gray-50 p-4 rounded-md shadow-sm border border-gray-200" data-id="element-12402">
          <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-5" data-id="element-12403">
            <div data-id="element-12404">
              <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700" data-id="element-12405">
                Category
              </label>
              <select id="category-filter" name="category-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.category} onChange={e => handleFilterChange('category', e.target.value)} data-id="element-12406">
                <option value="" data-id="element-12407">All Categories</option>
                {categories.map(category => <option key={category} value={category} data-id="element-12408">
                    {category}
                  </option>)}
              </select>
            </div>
            <div data-id="element-12409">
              <label htmlFor="manufacturer-filter" className="block text-sm font-medium text-gray-700" data-id="element-12410">
                Manufacturer
              </label>
              <select id="manufacturer-filter" name="manufacturer-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.manufacturer} onChange={e => handleFilterChange('manufacturer', e.target.value)} data-id="element-12411">
                <option value="" data-id="element-12412">All Manufacturers</option>
                {manufacturers.map(manufacturer => <option key={manufacturer} value={manufacturer} data-id="element-12413">
                    {manufacturer}
                  </option>)}
              </select>
            </div>
            <div data-id="element-12414">
              <label htmlFor="min-price-filter" className="block text-sm font-medium text-gray-700" data-id="element-12415">
                Min Price
              </label>
              <input type="number" id="min-price-filter" name="min-price-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.minPrice} onChange={e => handleFilterChange('minPrice', e.target.value)} min="0" step="0.01" data-id="element-12416" />
            </div>
            <div data-id="element-12417">
              <label htmlFor="max-price-filter" className="block text-sm font-medium text-gray-700" data-id="element-12418">
                Max Price
              </label>
              <input type="number" id="max-price-filter" name="max-price-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.maxPrice} onChange={e => handleFilterChange('maxPrice', e.target.value)} min="0" step="0.01" data-id="element-12419" />
            </div>
            <div data-id="element-12420">
              <label htmlFor="availability-filter" className="block text-sm font-medium text-gray-700" data-id="element-12421">
                Availability
              </label>
              <select id="availability-filter" name="availability-filter" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.availability} onChange={e => handleFilterChange('availability', e.target.value)} data-id="element-12422">
                <option value="" data-id="element-12423">All</option>
                <option value="in-stock" data-id="element-12424">In Stock</option>
                <option value="out-of-stock" data-id="element-12425">Out of Stock</option>
              </select>
            </div>
          </div>
        </div>}
      {/* Products table */}
      <ProductTable products={filteredProducts} schema={schema} onEdit={onEditProduct} onToggleStatus={onToggleStatus} locale={locale} data-id="element-12426" />
    </div>;
}